import { getAppliedTheme } from '@dashboard/lib/theme';
import type { TrickKey } from './outcomes';

const HIGHLIGHT_NAME = 'ghost-stolen';

const stopped = (signal: AbortSignal) =>
  new Promise<void>(resolve => {
    if (signal.aborted) resolve();
    else signal.addEventListener('abort', () => resolve(), { once: true });
  });

// Settles when the step ends or the seller stops the trick, whichever comes first.
const until = (signal: AbortSignal, step: Promise<unknown>) =>
  Promise.race([
    step.then(
      () => {},
      () => {},
    ),
    stopped(signal),
  ]);

const wait = (signal: AbortSignal, ms: number) =>
  until(signal, new Promise(resolve => setTimeout(resolve, ms)));

const prefersReducedMotion = () =>
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

function addLayer(className = '') {
  const layer = document.createElement('div');
  layer.setAttribute('aria-hidden', 'true');
  layer.className = className;
  Object.assign(layer.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '100',
    pointerEvents: 'none',
  });
  document.body.appendChild(layer);
  return layer;
}

function emoji(char: string, size: number) {
  const el = document.createElement('span');
  el.textContent = char;
  Object.assign(el.style, {
    position: 'absolute',
    left: '0',
    top: '0',
    fontSize: `${size}px`,
    lineHeight: '1',
    willChange: 'transform',
  });
  return el;
}

async function haunted(signal: AbortSignal) {
  const root = document.documentElement;
  const wasDark = root.classList.contains('dark');
  root.classList.add('dark');
  const overlay = addLayer('haunted-overlay');
  try {
    await wait(signal, 5000);
  } finally {
    overlay.remove();
    if (!wasDark && getAppliedTheme() !== 'dark') root.classList.remove('dark');
  }
}

type Word = { range: Range; rect: DOMRect; text: string };

function findVisibleWord(): Word | null {
  const root = document.querySelector('main') ?? document.body;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: node =>
      node.parentElement?.closest(
        'input, textarea, [contenteditable], script, style, [aria-hidden="true"]',
      )
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  const words: Word[] = [];
  for (let node = walker.nextNode(); node && words.length < 300; node = walker.nextNode()) {
    for (const match of (node as Text).data.matchAll(/\p{L}{4,}/gu)) {
      const range = document.createRange();
      range.setStart(node, match.index);
      range.setEnd(node, match.index + match[0].length);
      const rect = range.getBoundingClientRect();
      const onScreen =
        rect.width > 0 &&
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.right <= window.innerWidth;
      if (onScreen) words.push({ range, rect, text: match[0] });
    }
  }
  return words.length ? words[Math.floor(Math.random() * words.length)] : null;
}

// CSS.highlights hides the word without touching the text node React owns.
function hideWord(range: Range) {
  const highlights = (window.CSS as { highlights?: Map<string, unknown> } | undefined)?.highlights;
  if (!highlights || typeof Highlight === 'undefined') return () => {};
  highlights.set(HIGHLIGHT_NAME, new Highlight(range));
  return () => highlights.delete(HIGHLIGHT_NAME);
}

async function ghost(signal: AbortSignal) {
  const word = findVisibleWord();
  const layer = addLayer();
  const ghostEl = emoji('👻', 44);
  const carried = document.createElement('span');
  if (word) {
    const style = getComputedStyle(word.range.startContainer.parentElement!);
    carried.textContent = word.text;
    Object.assign(carried.style, {
      position: 'absolute',
      top: '40px',
      left: '30px',
      font: style.font,
      color: style.color,
      whiteSpace: 'nowrap',
      opacity: '0',
    });
    ghostEl.appendChild(carried);
  }
  layer.appendChild(ghostEl);

  const target = word
    ? { x: word.rect.left - 30, y: word.rect.top - 40 }
    : { x: window.innerWidth / 2, y: window.innerHeight / 3 };
  const offLeft = { x: -120, y: target.y };
  const offRight = { x: window.innerWidth + 40, y: Math.max(0, target.y - 80) };
  const at = ({ x, y }: { x: number; y: number }, extra = '') =>
    `translate(${x}px, ${y}px) ${extra}`;
  const fly = (from: typeof target, to: typeof target, duration: number) =>
    until(
      signal,
      ghostEl.animate(
        [
          { transform: at(from) },
          {
            transform: at({ x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 - 30 }, 'rotate(-8deg)'),
          },
          { transform: at(to) },
        ],
        { duration, easing: 'ease-in-out', fill: 'forwards' },
      ).finished,
    );

  let showWord = () => {};
  try {
    if (prefersReducedMotion()) {
      ghostEl.style.transform = at(target);
      if (word) showWord = hideWord(word.range);
      await until(
        signal,
        ghostEl.animate([{ opacity: 0 }, { opacity: 0.9 }, { opacity: 0.9 }, { opacity: 0 }], {
          duration: 3000,
        }).finished,
      );
      return;
    }
    await fly(offLeft, target, 1200);
    if (signal.aborted) return;
    if (word) {
      showWord = hideWord(word.range);
      carried.style.opacity = '1';
    }
    await fly(target, offRight, 1400);
    await wait(signal, 900);
    await fly(offRight, target, 1200);
    if (signal.aborted) return;
    showWord();
    showWord = () => {};
    carried.style.opacity = '0';
    await until(
      signal,
      ghostEl.animate(
        [
          { transform: at(target), opacity: 1 },
          { transform: at({ x: target.x - 120, y: target.y - 160 }), opacity: 0 },
        ],
        { duration: 800, easing: 'ease-in', fill: 'forwards' },
      ).finished,
    );
  } finally {
    showWord();
    layer.remove();
  }
}

async function bats(signal: AbortSignal) {
  const layer = addLayer();
  const { innerWidth: width, innerHeight: height } = window;
  const rand = (min: number, max: number) => min + Math.random() * (max - min);
  const reduced = prefersReducedMotion();

  const flights = Array.from({ length: reduced ? 5 : 16 }, () => {
    const bat = emoji('🦇', rand(22, 46));
    layer.appendChild(bat);
    if (reduced) {
      bat.style.transform = `translate(${rand(0, width - 50)}px, ${rand(0, height - 50)}px)`;
      return bat.animate([{ opacity: 0 }, { opacity: 1 }, { opacity: 0 }], {
        duration: 2500,
        delay: rand(0, 600),
        fill: 'both',
      }).finished;
    }
    const leftToRight = Math.random() < 0.5;
    const startX = leftToRight ? -60 : width + 60;
    const endX = leftToRight ? width + 60 : -60;
    const baseY = rand(0, height * 0.8);
    const steps = 8;
    const frames = Array.from({ length: steps + 1 }, (_, i) => {
      const x = startX + ((endX - startX) * i) / steps;
      const y = baseY + Math.sin(i * 1.3) * rand(20, 60);
      const flap = i % 2 ? 'scaleY(0.55)' : 'scaleY(1)';
      const facing = leftToRight ? '' : 'scaleX(-1)';
      return { transform: `translate(${x}px, ${y}px) ${facing} ${flap}` };
    });
    return bat.animate(frames, {
      duration: rand(3000, 4500),
      delay: rand(0, 600),
      easing: 'linear',
      fill: 'both',
    }).finished;
  });

  try {
    await until(signal, Promise.all(flights));
  } finally {
    layer.remove();
  }
}

export const TRICKS: Record<TrickKey, (signal: AbortSignal) => Promise<void>> = {
  haunted,
  ghost,
  bats,
};
